import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Mail } from 'lucide-react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import BookCover from './BookCover';
import { toast } from 'sonner';
import { API } from '../utils/api';

// Positions for the 6 floating slots: 3 left, 3 right
const LEFT_POSITIONS = [
{ right: 30, top: -10, size: 52, rotate: 5 },
{ right: 160, top: 85, size: 46, rotate: -7 },
{ right: 40, top: 185, size: 50, rotate: 3 }];

const RIGHT_POSITIONS = [
{ left: 30, top: -10, size: 52, rotate: -5 },
{ left: 160, top: 85, size: 46, rotate: -2 },
{ left: 40, top: 185, size: 50, rotate: 7 }];


// Cycle order: left0, right0, left1, right1, left2, right2
const CYCLE_ORDER = [0, 3, 1, 4, 2, 5];

const pickRandom = (books, exclude) => {
  if (books.length === 0) return null;
  const available = books.filter((b) => !exclude.includes(b.slug || b.id));
  if (available.length === 0) {
    // Only allow repeat if 4 or fewer books total
    if (books.length <= 4) return books[Math.floor(Math.random() * books.length)];
    return null;
  }
  return available[Math.floor(Math.random() * available.length)];
};

const FloatingSlot = ({ book, position, side, fading }) => {
  const style = side === 'left' ?
  { right: position.right, top: position.top, width: position.size, height: position.size * 1.33 } :
  { left: position.left, top: position.top, width: position.size, height: position.size * 1.33 };

  return (
    <div className="absolute" style={style}>
      <div
        className="w-full h-full transition-all duration-700 ease-in-out"
        style={{
          opacity: fading ? 0 : 1,
          transform: fading ?
          `scale(0.85) rotate(${position.rotate - 3}deg)` :
          `scale(1) rotate(${position.rotate}deg)`
        }}>

        <div className="relative w-full overflow-hidden rounded-lg shadow-sm" style={{ aspectRatio: '3/4' }}>
          <BookCover book={book} hideTitle />
        </div>
      </div>
    </div>);

};

const HeroSection = ({ books = [] }) => {
  const [email, setEmail] = useState('');
  const [subtitleVisible, setSubtitleVisible] = useState(false);
  const [slots, setSlots] = useState([]);
  const [fadingSlot, setFadingSlot] = useState(-1);
  const stepRef = useRef(0);
  const booksRef = useRef(books);

  // Keep booksRef current
  useEffect(() => {booksRef.current = books;}, [books]);

  // Initialize slots when books are available
  useEffect(() => {
    if (books.length === 0) return;
    const initial = [];
    for (let i = 0; i < 6; i++) {
      initial.push(books[i % books.length]);
    }
    setSlots(initial);
  }, [books]);

  // Cycling animation
  useEffect(() => {
    if (slots.length === 0) return;

    const interval = setInterval(() => {
      // Alternate: left(0), right(3), left(1), right(4), left(2), right(5)
      const cycleOrder = [0, 3, 1, 4, 2, 5];
      const slotIdx = cycleOrder[stepRef.current % 6];

      // Phase 1: Fade out (700ms CSS transition)
      setFadingSlot(slotIdx);

      // Phase 2: After fully invisible, swap book data (still hidden)
      setTimeout(() => {
        setSlots((prev) => {
          const newSlots = [...prev];
          const usedSlugs = newSlots.filter(Boolean).map((s) => s.slug || s.id);
          const newBook = pickRandom(booksRef.current, usedSlugs);
          if (newBook) newSlots[slotIdx] = newBook;
          return newSlots;
        });
      }, 800);

      // Phase 3: After React renders new book, fade in
      setTimeout(() => {
        setFadingSlot(-1);
      }, 950);

      stepRef.current++;
    }, 1500);

    return () => clearInterval(interval);
  }, [slots.length]);

  useEffect(() => {
    const timer = setTimeout(() => setSubtitleVisible(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const captchaToken = executeRecaptcha ? await executeRecaptcha('subscribe') : '';
      const r = await fetch(`${API}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, captcha_token: captchaToken }),
      });
      if (r.ok) {
        toast.success('¡Suscrito!', { description: 'Recibirás nuevos libritos cada semana.' });
        setEmail('');
      } else {
        const data = await r.json();
        toast.error(data.detail || 'Error al suscribirse');
      }
    } catch {
      toast.error('Error de conexión');
    }
  };

  const leftSlots = slots.slice(0, 3).filter(Boolean);
  const rightSlots = slots.slice(3, 6).filter(Boolean);

  return (
    <section className="relative py-12 sm:py-16 px-4 md:px-0 overflow-hidden">
      {/* Mobile floating books — 3 left, 3 right like icon.museum */}
      {slots.length > 0 &&
      <div className="absolute inset-0 md:hidden pointer-events-none" aria-hidden="true">
          {[
        { top: '0%', left: '12px', width: 40, idx: 0, rotate: -10, opacity: 0.20 },
        { top: '36%', left: '-12px', width: 36, idx: 1, rotate: -5, opacity: 0.20 },
        { top: '72%', left: '14px', width: 34, idx: 2, rotate: -15, opacity: 0.20 },
        { top: '2%', right: '14px', width: 38, idx: 3, rotate: 6, opacity: 0.20 },
        { top: '38%', right: '-10px', width: 42, idx: 4, rotate: 12, opacity: 0.20 },
        { top: '74%', right: '12px', width: 36, idx: 5, rotate: 8, opacity: 0.20 },
        ].
        map((item) => {
        if (!slots[item.idx]) return null;
        return (
        <div
          key={item.idx}
          className="absolute transition-all duration-700 ease-in-out"
          style={{
            top: item.top, left: item.left, right: item.right,
            width: item.width, height: item.width * 1.33,
            opacity: fadingSlot === item.idx ? 0 : item.opacity,
            transform: `rotate(${item.rotate}deg) scale(${fadingSlot === item.idx ? 0.85 : 1})`
          }}>
              <div className="relative w-full h-full overflow-hidden rounded-md">
                <BookCover book={slots[item.idx]} hideTitle />
              </div>
            </div>
        );})}
        </div>
      }

      <div className="flex items-center justify-center gap-8 lg:gap-16">
        {/* Left floating books (desktop) */}
        <div className="hidden md:block flex-1">
          <div className="relative h-72">
            {leftSlots.map((book, i) =>
            <FloatingSlot
              key={`left-${i}`}
              book={book}
              position={LEFT_POSITIONS[i]}
              side="left"
              fading={fadingSlot === i} />

            )}
          </div>
        </div>

        {/* Center content */}
        <div className="relative text-center max-w-xl">
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-5xl leading-tight">
            Libritos de Fe para los más pequeños
            <br className="hidden sm:block" />
            {' '}
          </h1>

          <form onSubmit={handleSubmit} noValidate className="mx-auto mt-4 sm:mt-8 max-w-sm">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="h-9 w-full px-3 text-sm bg-secondary rounded-xl outline-none focus:ring-2 focus:ring-blue-500/30 transition-shadow placeholder:text-muted-foreground/50"
                  placeholder="Tu correo electrónico" name="email" />

              </div>
              <button type="submit" className="relative h-9 px-4 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-all duration-200 active:scale-[0.97] flex items-center gap-2">
                <Mail className="size-[18px]" />
                Suscribirme
              </button>
            </div>
            <div className="mt-3 text-sm text-muted-foreground/60 overflow-hidden">
              <p className="transition-all duration-500 ease-out" style={{
                opacity: subtitleVisible ? 1 : 0,
                filter: subtitleVisible ? 'blur(0px)' : 'blur(4px)',
                transform: subtitleVisible ? 'scale(1)' : 'scale(0.985)'
              }}>Recibe nuevas historias en tu correo✨

              </p>
            </div>
          </form>
        </div>

        {/* Right floating books (desktop) */}
        <div className="hidden md:block flex-1">
          <div className="relative h-72">
            {rightSlots.map((book, i) =>
            <FloatingSlot
              key={`right-${i}`}
              book={book}
              position={RIGHT_POSITIONS[i]}
              side="right"
              fading={fadingSlot === i + 3} />

            )}
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;